import { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import RecipeBasicInfo from './RecipeBasicInfo';
import RecipeIngredients from './RecipeIngredients';
import RecipeImageUpload from './RecipeImageUpload';

const RecipeForm = ({ 
  recipeData, 
  currency,
  onInputChange, 
  onIngredientChange, 
  onAddIngredient, 
  onRemoveIngredient, 
  onSelectSuggestion,
  onCancel,
  onSubmit,
  ingredientSuggestions,
  showSuggestions,
  activeIngredientIndex,
  setShowSuggestions,
  setActiveIngredientIndex,
  setIngredientSuggestions,
  isEditing,
  isSubmitting
}) => {
  const [localRecipe, setLocalRecipe] = useState(recipeData);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    setLocalRecipe(recipeData);
    if (recipeData.image_url) {
      setImagePreview(recipeData.image_url);
    } else {
      setImagePreview(null);
    }
    setImageFile(null);
  }, [recipeData]);

  const handleLocalInputChange = (e) => {
    const { name, value } = e.target;
    setLocalRecipe(prev => ({ ...prev, [name]: value }));
    onInputChange(e);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!localRecipe.name || !localRecipe.preparationTime || !localRecipe.instructions || !localRecipe.cost) {
      alert('Please fill in all required fields (Name, Preparation Time, Cost, Instructions)');
      return;
    }

    const prepTime = parseInt(localRecipe.preparationTime);
    const cost = parseFloat(localRecipe.cost);
    if (isNaN(prepTime) ){
      alert('Preparation Time must be a number (in minutes)');
      return;
    }
    if (isNaN(cost)) {
      alert('Cost must be a number');
      return;
    
    }

    if (localRecipe.expiryDuration) {
      const expiryDays = parseInt(localRecipe.expiryDuration);
      if (isNaN(expiryDays)) {
        alert('Expiry Duration must be a number (in days)');
        return;
      }
    }

    const hasEmptyIngredients = localRecipe.ingredients.some(ing => 
      !ing.name || ing.quantity === '' || isNaN(ing.quantity) || !ing.unit
    );
    
    if (hasEmptyIngredients) {
      alert('Please fill in all fields for each ingredient (Name, Quantity, Unit)');
      return;
    }

    const formData = new FormData();
    
    formData.append('name', localRecipe.name);
    formData.append('preparation_time', prepTime.toString());
    formData.append('cost', cost.toString());
    formData.append('instructions', localRecipe.instructions);
    
    if (localRecipe.description) formData.append('description', localRecipe.description);
    if (localRecipe.expiryDuration) {
      const expiryDays = parseInt(localRecipe.expiryDuration);
      if (!isNaN(expiryDays) && expiryDays > 0) {
        formData.append('expiry_duration', expiryDays.toString());
      }
    }
    
    const ingredientsData = localRecipe.ingredients.map(ing => ({
      name: ing.name,
      quantity: parseFloat(ing.quantity),
      unit: ing.unit
    }));
    formData.append('ingredients', JSON.stringify(ingredientsData));

    if (imageFile) {
      formData.append('image', imageFile);
    } else if (!imagePreview && localRecipe.image_url) {
      formData.append('remove_image', 'true');
    }

    try {
      if (isEditing && localRecipe.id) {
        await onSubmit(formData, localRecipe.id);
      } else {
        await onSubmit(formData);
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert(`Error: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center border-b p-4 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold">
            {isEditing ? 'Edit Recipe' : 'Add New Recipe'}
          </h2>
          <button 
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            disabled={isSubmitting}
          >
            <FiX size={24} />
          </button>
        </div>

        <form className="p-6 space-y-4" onSubmit={handleSubmit}>
          <RecipeBasicInfo 
            localRecipe={localRecipe} 
            handleLocalInputChange={handleLocalInputChange} 
            isSubmitting={isSubmitting}
            currency={currency}
          />

          <RecipeIngredients
            localRecipe={localRecipe}
            handleLocalIngredientChange={onIngredientChange}
            onAddIngredient={onAddIngredient}
            onRemoveIngredient={onRemoveIngredient}
            onSelectSuggestion={onSelectSuggestion}
            ingredientSuggestions={ingredientSuggestions}
            showSuggestions={showSuggestions}
            activeIngredientIndex={activeIngredientIndex}
            setShowSuggestions={setShowSuggestions}
            setActiveIngredientIndex={setActiveIngredientIndex}
            setIngredientSuggestions={setIngredientSuggestions}
            isSubmitting={isSubmitting}
          />

          <RecipeImageUpload
            imagePreview={imagePreview}
            handleImageChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                const file = e.target.files[0];
                setImageFile(file);
                
                const reader = new FileReader();
                reader.onloadend = () => {
                  setImagePreview(reader.result);
                };
                reader.readAsDataURL(file);
              }
            }}
            handleRemoveImage={() => {
              setImageFile(null);
              setImagePreview(null);
              setLocalRecipe(prev => ({ ...prev, image_url: null }));
            }}
            isSubmitting={isSubmitting}
          />

          <div className="flex justify-end gap-3 pt-4 sticky bottom-0 bg-white py-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors ${
                isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : (isEditing ? 'Update Recipe' : 'Add Recipe')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecipeForm;